import React, { type JSX } from "react";

import type {
  AdminRecognitionComparisonMatrix,
  AdminRecognitionComparisonRow,
  AdminRecognitionRawJsonComparison,
} from "../../contracts/admin-read-models";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import type { RecognitionModelComparisonCardViewModel } from "./recognition-model-comparison-view-model";

export type RecognitionModelComparisonMatrixProps = {
  cards: readonly RecognitionModelComparisonCardViewModel[];
  matrix: AdminRecognitionComparisonMatrix | AdminRecognitionRawJsonComparison;
  locale: AdminDashboardLocale;
  title: string;
};

export function RecognitionModelComparisonMatrix(props: RecognitionModelComparisonMatrixProps): JSX.Element {
  const copy = createCopy(props.locale);
  const differingRows = props.matrix.rows.filter((row) => row.verdict !== "same");
  const matchingRows = props.matrix.rows.filter((row) => row.verdict === "same");

  return (
    <section className="recognition-comparison-matrix" aria-label={props.title}>
      <style>{`
        .recognition-comparison-matrix { display: grid; gap: 12px; min-width: 0; }
        .recognition-comparison-matrix__desktop { display: block; overflow-x: auto; }
        .recognition-comparison-matrix__mobile { display: none; }
        .recognition-comparison-matrix__table { width: 100%; min-width: 840px; border-collapse: separate; border-spacing: 0; }
        .recognition-comparison-matrix__table th, .recognition-comparison-matrix__table td { padding: 10px; border-bottom: 1px solid rgba(148,163,184,.25); text-align: left; vertical-align: top; overflow-wrap: anywhere; }
        .recognition-comparison-matrix__table thead th { position: sticky; top: 0; background: #f8fafc; z-index: 1; }
        .recognition-comparison-matrix__item { min-width: 130px; }
        .recognition-comparison-matrix__model { min-width: 118px; }
        .recognition-comparison-matrix__verdict { min-width: 76px; font-weight: 800; }
        .recognition-comparison-matrix__mobile-row { display: grid; gap: 10px; padding: 14px; border: 1px solid rgba(148,163,184,.28); border-radius: 14px; background: #fff; }
        .recognition-comparison-matrix__mobile-cell { display: grid; grid-template-columns: minmax(110px, .8fr) minmax(0, 1.2fr); gap: 10px; padding-top: 8px; border-top: 1px solid rgba(148,163,184,.2); }
        @media (max-width: 600px) {
          .recognition-comparison-matrix__desktop { display: none; }
          .recognition-comparison-matrix__mobile { display: grid; gap: 12px; }
        }
      `}</style>
      <div>
        <h3 style={{ margin: 0 }}>{props.title}</h3>
        <p style={{ margin: "5px 0 0", color: "#64748b" }}>{copy.differencesFirst}</p>
      </div>
      {differingRows.length > 0 ? <MatrixRows cards={props.cards} copy={copy} rows={differingRows} /> : <p style={emptyStyle}>{copy.noDifferences}</p>}
      {matchingRows.length > 0 ? (
        <details>
          <summary style={{ cursor: "pointer", fontWeight: 800 }}>{copy.showMatching} ({matchingRows.length})</summary>
          <div style={{ marginTop: "10px" }}><MatrixRows cards={props.cards} copy={copy} rows={matchingRows} /></div>
        </details>
      ) : null}
    </section>
  );
}

function MatrixRows(props: {
  cards: readonly RecognitionModelComparisonCardViewModel[];
  copy: ReturnType<typeof createCopy>;
  rows: readonly AdminRecognitionComparisonRow[];
}): JSX.Element {
  return (
    <>
      <div className="recognition-comparison-matrix__desktop" data-layout="comparison-desktop">
        <table className="recognition-comparison-matrix__table">
          <thead>
            <tr>
              <th className="recognition-comparison-matrix__item">{props.copy.item}</th>
              {props.cards.map((card) => <th className="recognition-comparison-matrix__model" key={card.variantKey}>{card.rank}. {card.model}{card.isReturned ? <small style={{ display: "block", color: "#047857" }}>{props.copy.returned}</small> : null}</th>)}
              <th>{props.copy.verdict}</th>
            </tr>
          </thead>
          <tbody>
            {props.rows.map((row) => (
              <tr key={row.rowKey}>
                <th className="recognition-comparison-matrix__item">{row.label}{row.referenceRange ? <small style={{ display: "block", color: "#64748b" }}>{props.copy.reference} {row.referenceRange}</small> : null}</th>
                {props.cards.map((card) => <td key={card.variantKey}>{renderCell(row, card.variantKey, props.copy)}</td>)}
                <td className="recognition-comparison-matrix__verdict">{props.copy.verdicts[row.verdict]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="recognition-comparison-matrix__mobile" data-layout="comparison-mobile">
        {props.rows.map((row) => (
          <article className="recognition-comparison-matrix__mobile-row" key={row.rowKey}>
            <div><strong>{row.label}</strong><span style={{ display: "block", marginTop: "4px", color: "#9a3412", fontWeight: 800 }}>{props.copy.verdicts[row.verdict]}</span></div>
            {props.cards.map((card) => <div className="recognition-comparison-matrix__mobile-cell" key={card.variantKey}><strong>{card.rank}. {card.model}</strong><span>{renderCell(row, card.variantKey, props.copy)}</span></div>)}
          </article>
        ))}
      </div>
    </>
  );
}

function renderCell(
  row: AdminRecognitionComparisonRow,
  variantKey: string,
  copy: ReturnType<typeof createCopy>,
): string {
  const cell = row.cells.find((candidate) => candidate.variantKey === variantKey);
  if (!cell) return copy.missing;
  if (cell.state === "missing") return copy.missing;
  if (cell.state === "not_executed") return copy.notExecuted;
  if (cell.state === "unavailable") return copy.unavailable;
  if (cell.displayValue) return cell.displayValue;
  if (cell.value === undefined) return copy.missing;
  try {
    return typeof cell.value === "string" ? cell.value : JSON.stringify(cell.value);
  } catch {
    return copy.unavailable;
  }
}

function createCopy(locale: AdminDashboardLocale) {
  return {
    item: pickAdminDashboardLocaleText(locale, { en: "Item", ko: "항목" }),
    verdict: pickAdminDashboardLocaleText(locale, { en: "Result", ko: "판정" }),
    returned: pickAdminDashboardLocaleText(locale, { en: "Returned model", ko: "반환 모델" }),
    reference: pickAdminDashboardLocaleText(locale, { en: "Reference", ko: "기준" }),
    missing: pickAdminDashboardLocaleText(locale, { en: "Missing", ko: "누락" }),
    notExecuted: pickAdminDashboardLocaleText(locale, { en: "Not executed", ko: "미실행" }),
    unavailable: pickAdminDashboardLocaleText(locale, { en: "No response", ko: "응답 없음" }),
    noDifferences: pickAdminDashboardLocaleText(locale, { en: "No differing items", ko: "차이 항목 없음" }),
    showMatching: pickAdminDashboardLocaleText(locale, { en: "Show matching items", ko: "일치 항목 보기" }),
    differencesFirst: pickAdminDashboardLocaleText(locale, { en: "Differences and missing values are shown first.", ko: "차이와 누락 항목을 먼저 보여줍니다." }),
    verdicts: {
      same: pickAdminDashboardLocaleText(locale, { en: "Match", ko: "일치" }),
      different: pickAdminDashboardLocaleText(locale, { en: "Different", ko: "차이" }),
      partial: pickAdminDashboardLocaleText(locale, { en: "Partial difference", ko: "부분 차이" }),
      unavailable: pickAdminDashboardLocaleText(locale, { en: "Unavailable", ko: "비교 불가" }),
    },
  };
}

const emptyStyle = {
  margin: 0,
  padding: "12px",
  borderRadius: "12px",
  background: "#f8fafc",
  color: "#64748b",
} as const;
